import { logError, logInfo } from '../logger';

export const validateGeoJSON = (data) => {
  try {
    // Basic structure validation
    if (!data || typeof data !== 'object') {
      throw new Error('Invalid GeoJSON: must be an object');
    }

    // Handle both FeatureCollection and single Feature/Geometry
    if (data.type === 'FeatureCollection') {
      if (!Array.isArray(data.features)) {
        throw new Error('Invalid GeoJSON: features must be an array');
      }

      // Validate each feature
      data.features.forEach((feature, index) => validateFeature(feature, index));
    } else if (data.type === 'Feature') {
      validateFeature(data, 0);
    } else if (['Point', 'LineString', 'Polygon'].includes(data.type)) {
      validateGeometry(data, 0);
    } else {
      throw new Error('Invalid GeoJSON: unsupported type');
    }

    logInfo('GeoJSON validation successful');
    return { isValid: true };
  } catch (error) {
    logError('GeoJSON validation failed:', error);
    return { 
      isValid: false, 
      error: error.message 
    };
  }
};

const validateFeature = (feature, index) => {
  if (!feature.type || feature.type !== 'Feature') {
    throw new Error(`Invalid feature at index ${index}: missing or invalid type`);
  }

  if (!feature.geometry) {
    throw new Error(`Invalid feature at index ${index}: missing geometry`);
  }

  validateGeometry(feature.geometry, index);

  // Ensure properties exist
  if (!feature.properties) {
    feature.properties = {};
  }
};

const validateGeometry = (geometry, index) => {
  if (!geometry.type || !geometry.coordinates) {
    throw new Error(`Invalid geometry at index ${index}: missing type or coordinates`);
  }

  switch (geometry.type) {
    case 'Point':
      validateCoordinates(geometry.coordinates, index);
      break;
    case 'LineString':
      if (!Array.isArray(geometry.coordinates)) {
        throw new Error(`Invalid LineString at index ${index}: coordinates must be an array`);
      }
      geometry.coordinates.forEach((coord, i) => validateCoordinates(coord, index, i));
      break;
    case 'Polygon':
      if (!Array.isArray(geometry.coordinates) || !Array.isArray(geometry.coordinates[0])) {
        throw new Error(`Invalid Polygon at index ${index}: coordinates must be an array of arrays`);
      }
      geometry.coordinates.forEach((ring, ringIndex) => {
        ring.forEach((coord, i) => validateCoordinates(coord, index, i, ringIndex));
      });
      break;
    default:
      throw new Error(`Invalid geometry at index ${index}: unsupported type ${geometry.type}`);
  }
};

const validateCoordinates = (coords, featureIndex, coordIndex = 0, ringIndex = 0) => {
  if (!Array.isArray(coords)) {
    throw new Error(`Invalid coordinates at feature ${featureIndex}, position ${coordIndex}`);
  }

  if (coords.length < 2) {
    throw new Error(`Incomplete coordinates at feature ${featureIndex}, position ${coordIndex}`);
  }

  const [lon, lat] = coords;
  if (typeof lon !== 'number' || typeof lat !== 'number' || isNaN(lon) || isNaN(lat)) {
    throw new Error(`Invalid coordinate values at feature ${featureIndex}, position ${coordIndex}`);
  }

  // Check coordinate bounds
  if (lon < -180 || lon > 180) {
    throw new Error(`Longitude out of bounds at feature ${featureIndex}, position ${coordIndex}: ${lon}`);
  }
  if (lat < -90 || lat > 90) {
    throw new Error(`Latitude out of bounds at feature ${featureIndex}, position ${coordIndex}: ${lat}`);
  }
};